import * as API from 'services/api';
import type { ApiResponse, ApiError } from 'services/api';
import type { Role } from 'constants/roles';

// =============================================================================
// GET /v2/account/users-management
// =============================================================================
export namespace GetUsersManagement {
  export interface User {
    id: number;
    email: string;
    username: string;
    first_name: string;
    last_name: string;
    role: Role;
    is_active: boolean;
    has_joined: boolean;
  }

  export type Response = ApiResponse<User[]>;
  export type Error = ApiError;
}

export const getUsersManagement =
  async (): Promise<GetUsersManagement.Response> => {
    const res = await API.get('/v2/account/users-management');
    return { data: res.data, status: res.status };
  };

// =============================================================================
// POST /v2/account/users-management
// =============================================================================
export namespace PostUsersManagement {
  export interface RequestBody {
    email: string;
    first_name: string;
    last_name: string;
    role: Role;
  }

  export interface ResponseData {
    id: number;
    email: string;
    username: string;
    first_name: string;
    last_name: string;
    role: Role;
    is_active: boolean;
    has_joined: boolean;
  }

  export type Response = ApiResponse<ResponseData>;
  export type Error = ApiError;
}

export const postUsersManagement = async (
  data: PostUsersManagement.RequestBody,
): Promise<PostUsersManagement.Response> => {
  const res = await API.post('/v2/account/users-management/', data);
  return { data: res.data, status: res.status };
};

// =============================================================================
// GET /v2/account/users-management/{user_id}/re-invite/
// =============================================================================
export namespace GetUserReInvite {
  export interface ResponseData {
    message: string;
  }

  export type Response = ApiResponse<ResponseData>;
  export type Error = ApiError;
}

export const getUserReInvite = async (
  userId: number,
): Promise<GetUserReInvite.Response> => {
  const res = await API.get(
    `/v2/account/users-management/${userId}/re-invite/`,
  );
  return { data: res.data, status: res.status };
};

// =============================================================================
// PATCH /v2/account/users-management/{user_id}/
// =============================================================================
export namespace PatchUsersManagementUser {
  export interface RequestBody {
    role: Role;
  }

  export interface ResponseData {
    id: number;
    email: string;
    username: string;
    first_name: string;
    last_name: string;
    role: Role;
    is_active: boolean;
    has_joined: boolean;
  }

  export type Response = ApiResponse<ResponseData>;
  export type Error = ApiError;
}

export const patchUsersManagementUser = async (
  userId: number,
  data: PatchUsersManagementUser.RequestBody,
): Promise<PatchUsersManagementUser.Response> => {
  const res = await API.patch(`/v2/account/users-management/${userId}/`, data);
  return { data: res.data, status: res.status };
};

// =============================================================================
// DELETE /v2/account/users-management/{user_id}/
// =============================================================================
export namespace DeleteUsersManagementUser {
  export interface ResponseData {
    message: string;
  }

  export type Response = ApiResponse<ResponseData>;
  export type Error = ApiError;
}

export const deleteUsersManagementUser = async (
  userId: number,
): Promise<DeleteUsersManagementUser.Response> => {
  const res = await API.remove(`/v2/account/users-management/${userId}/`);
  return { data: res.data, status: res.status };
};

// =============================================================================
// POST /v2/account/join/organization/
// =============================================================================
export namespace PostJoinOrganization {
  export interface RequestBody {
    first_name: string;
    last_name: string;
    token: string;
    new_password: string;
    confirm_password: string;
    tc_accepted?: boolean;
    tc_accepted_version?: string;
  }

  export interface ResponseData {
    jwt: string;
    refresh: string;
  }

  export type Response = ApiResponse<ResponseData>;
  export type Error = ApiError;
}

export const postJoinOrganization = async (
  data: PostJoinOrganization.RequestBody,
): Promise<PostJoinOrganization.Response> => {
  const res = await API.post('/v2/account/join-organization/', data);
  return { data: res.data, status: res.status };
};

// =============================================================================
// GET /v2/account/statistics/
// =============================================================================
export namespace GetStatistics {
  export interface ResponseData {
    plan_name: string;
    plan_start_date: string;
    plan_end_date: string;
    can_create_movie: boolean;
    can_create_prediction: boolean;
    movies_used: number;
    movies_limit: number;
    predictions_used: number;
    predictions_limit: number;
    per_movie_predictions_limit: number;
  }

  export type Response = ApiResponse<ResponseData>;
  export type Error = ApiError;
}

export const getStatistics = async (): Promise<GetStatistics.Response> => {
  const res = await API.get('/v2/account/statistics/');
  return { data: res.data, status: res.status };
};
